import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Input,
  Modal,
  Form,
  InputNumber,
  message,
  Row,
  Col,
  Statistic,
  Space,
  Tag,
  Typography,
  Select,
  DatePicker,
  Tabs,
  Descriptions,
  Badge,
  Divider
} from 'antd';
import {
  PlusOutlined,
  MinusOutlined,
  DollarOutlined,
  WalletOutlined,
  HistoryOutlined,
  ReloadOutlined,
  SearchOutlined,
  EyeOutlined,
  BankOutlined,
  RiseOutlined,
  FallOutlined,
  FilterOutlined,
  ExportOutlined
} from '@ant-design/icons';
import axios from 'axios';
import moment from 'moment';

const { Title, Text } = Typography;

// Create axios instance with JWT authentication (for admin)
const adminApi = axios.create({
  baseURL: '/api',
  timeout: 30000,
});

// Request interceptor to add JWT token
adminApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    config.headers['Content-Type'] = 'application/json';
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor to handle errors
adminApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('access_token');
      localStorage.removeItem('user');
      window.location.href = '/admin/login';
    }
    return Promise.reject(error);
  }
);
const { Option } = Select;
const { TabPane } = Tabs;
const { TextArea } = Input;
const { RangePicker } = DatePicker;

const CashInOut = () => {
  const [loading, setLoading] = useState(false);
  const [cashInData, setCashInData] = useState([]);
  const [cashOutData, setCashOutData] = useState([]);
  const [summary, setSummary] = useState({});
  const [activeTab, setActiveTab] = useState('cash-in');

  const [cashInPagination, setCashInPagination] = useState({ page: 1, pageSize: 20, total: 0 });
  const [cashOutPagination, setCashOutPagination] = useState({ page: 1, pageSize: 20, total: 0 });

  // Modal states
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);

  // Filter states
  const [filters, setFilters] = useState({
    search: '',
    transaction_type: null,
    status: null,
    date_range: null,
    merchant_id: null,
  });

  useEffect(() => {
    if (activeTab === 'cash-in') {
      fetchCashInData(1, cashInPagination.pageSize);
    } else if (activeTab === 'cash-out') {
      fetchCashOutData(1, cashOutPagination.pageSize);
    }
    fetchSummary();
  }, [filters, activeTab]);

  const fetchCashInData = async (page = 1, pageSize = 20) => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('limit', pageSize);

      if (filters.search) params.append('search', filters.search);
      if (filters.transaction_type) params.append('transaction_type', filters.transaction_type);
      if (filters.status) params.append('status', filters.status);
      if (filters.merchant_id) params.append('merchant_id', filters.merchant_id);
      if (filters.date_range && filters.date_range[0]) {
        params.append('date_from', filters.date_range[0].format('YYYY-MM-DD'));
        params.append('date_to', filters.date_range[1].format('YYYY-MM-DD'));
      }

      const response = await adminApi.get(`/settlements/cash-in-simple?${params.toString()}`);
      setCashInData(response.data.data || []);
      const pag = response.data.pagination || {};
      setCashInPagination({ page: pag.page || page, pageSize: pag.limit || pageSize, total: pag.total || 0 });
    } catch (error) {
      message.error('Gagal memuat data cash in');
      console.error('Fetch cash in error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCashOutData = async (page = 1, pageSize = 20) => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('limit', pageSize);

      if (filters.search) params.append('search', filters.search);
      if (filters.transaction_type) params.append('transaction_type', filters.transaction_type);
      if (filters.status) params.append('status', filters.status);
      if (filters.merchant_id) params.append('merchant_id', filters.merchant_id);
      if (filters.date_range && filters.date_range[0]) {
        params.append('date_from', filters.date_range[0].format('YYYY-MM-DD'));
        params.append('date_to', filters.date_range[1].format('YYYY-MM-DD'));
      }

      const response = await adminApi.get(`/settlements/cash-out-simple?${params.toString()}`);
      setCashOutData(response.data.data || []);
      const pag = response.data.pagination || {};
      setCashOutPagination({ page: pag.page || page, pageSize: pag.limit || pageSize, total: pag.total || 0 });
    } catch (error) {
      message.error('Gagal memuat data cash out');
      console.error('Fetch cash out error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSummary = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.date_range && filters.date_range[0]) {
        params.append('date_from', filters.date_range[0].format('YYYY-MM-DD'));
        params.append('date_to', filters.date_range[1].format('YYYY-MM-DD'));
      }

      const response = await adminApi.get(`/settlements/cash-flow-summary?${params.toString()}`);
      setSummary(response.data.summary || {});
    } catch (error) {
      console.error('Fetch summary error:', error);
    }
  };

  const handleViewDetail = (record) => {
    setSelectedRecord(record);
    setDetailModalVisible(true);
  };

  const formatCurrency = (amount) => {
    const numericAmount = Number(amount);
    if (isNaN(numericAmount)) return 'Rp 0';

    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
    }).format(numericAmount);
  };

  const getTransactionTypeTag = (type, flow) => {
    const configs = {
      // Cash In types
      'qris_payment': { color: 'green', text: 'QRIS Payment' },
      'va_payment': { color: 'blue', text: 'VA Payment' },
      'ewallet_payment': { color: 'purple', text: 'E-Wallet Payment' },
      'deposit': { color: 'cyan', text: 'Deposit' },
      'refund_received': { color: 'orange', text: 'Refund Received' },
      'adjustment_in': { color: 'gold', text: 'Adjustment (In)' },

      // Cash Out types
      'topup': { color: 'volcano', text: 'Top Up' },
      'bifast_transfer': { color: 'red', text: 'BIFAST Transfer' },
      'bill_payment': { color: 'magenta', text: 'Bill Payment' },
      'withdrawal': { color: 'geekblue', text: 'Withdrawal' },
      'fee_deduction': { color: 'gray', text: 'Fee Deduction' },
      'adjustment_out': { color: 'default', text: 'Adjustment (Out)' }
    };

    const config = configs[type] || { color: 'default', text: type };
    return <Tag color={config.color}>{config.text}</Tag>;
  };

  const getStatusTag = (status) => {
    const colors = {
      'success': 'green',
      'pending': 'orange',
      'failed': 'red',
      'expired': 'volcano',
      'processing': 'blue',
      'cancelled': 'default'
    };
    return <Tag color={colors[status] || 'default'}>{status?.toUpperCase()}</Tag>;
  };

  // Common columns for both tables
  const getCommonColumns = (isIn = true) => [
    {
      title: 'Waktu',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => moment(date).format('DD/MM/YYYY HH:mm'),
      sorter: true,
      width: 150,
    },
    {
      title: 'Merchant',
      key: 'merchant',
      render: (_, record) => (
        <div>
          <div><strong>{record.merchant_name}</strong></div>
          <div style={{ fontSize: '12px', color: '#666' }}>ID: {record.merchant_id}</div>
        </div>
      ),
      width: 180,
    },
    ...(!isIn ? [{
      title: 'Tujuan',
      key: 'destination',
      width: 180,
      render: (_, record) => {
        if (!record.destination_number && !record.destination_name) return <Text type="secondary">-</Text>;
        return (
          <div>
            <div style={{ fontFamily: 'monospace', fontWeight: 600 }}>{record.destination_number || '-'}</div>
            <div style={{ fontSize: 12, color: '#555' }}>{record.destination_name || ''}</div>
            {record.destination_bank && <div style={{ fontSize: 11, color: '#999' }}>{record.destination_bank}</div>}
          </div>
        );
      },
    }] : []),
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount) => (
        <Text strong style={{ color: isIn ? '#52c41a' : '#ff4d4f', fontSize: '16px' }}>
          {isIn ? '+' : '-'}{formatCurrency(Math.abs(amount))}
        </Text>
      ),
      sorter: true,
      align: 'right',
    },
    {
      title: 'Reference',
      dataIndex: 'reference_id',
      key: 'reference_id',
      ellipsis: true,
      render: (ref) => ref || '-',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => getStatusTag(status),
      filters: [
        { text: 'Success', value: 'success' },
        { text: 'Pending', value: 'pending' },
        { text: 'Failed', value: 'failed' },
        { text: 'Expired', value: 'expired' },
        { text: 'Processing', value: 'processing' },
      ],
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            size="small"
            icon={<EyeOutlined />}
            onClick={() => handleViewDetail(record)}
          >
            Detail
          </Button>
        </Space>
      ),
      width: 100,
    },
  ];

  const resetFilters = () => {
    setFilters({
      search: '',
      transaction_type: null,
      status: null,
      date_range: null,
      merchant_id: null,
    });
  };

  const exportToCSV = async () => {
    const isCashIn = activeTab === 'cash-in';
    const tabLabel = isCashIn ? 'Cash_In' : 'Cash_Out';
    const endpoint = isCashIn ? '/settlements/cash-in-simple' : '/settlements/cash-out-simple';

    message.loading({ content: 'Mengambil data untuk export...', key: 'csv-export' });
    let data = [];
    try {
      const params = new URLSearchParams();
      params.append('page', '1');
      params.append('limit', '50000');
      if (filters.search) params.append('search', filters.search);
      if (filters.transaction_type) params.append('transaction_type', filters.transaction_type);
      if (filters.status) params.append('status', filters.status);
      if (filters.merchant_id) params.append('merchant_id', filters.merchant_id);
      if (filters.date_range && filters.date_range[0]) {
        params.append('date_from', filters.date_range[0].format('YYYY-MM-DD'));
        params.append('date_to', filters.date_range[1].format('YYYY-MM-DD'));
      }
      const response = await adminApi.get(`${endpoint}?${params.toString()}`);
      data = response.data.data || [];
    } catch (error) {
      message.error({ content: 'Gagal mengambil data untuk export', key: 'csv-export' });
      return;
    }

    if (data.length === 0) {
      message.warning({ content: 'Tidak ada data untuk di-export', key: 'csv-export' });
      return;
    }

    const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;

    const headers = isCashIn
      ? ['Waktu', 'Merchant', 'Merchant ID', 'Tipe Transaksi', 'Amount', 'Reference', 'Status', 'Description']
      : ['Waktu', 'Merchant', 'Merchant ID', 'Tipe Transaksi', 'Amount', 'Nomor Tujuan', 'Nama Tujuan', 'Bank/Dompet', 'Reference', 'Status', 'Description'];

    const rows = data.map(record => {
      const base = [
        moment(record.created_at).format('DD/MM/YYYY HH:mm'),
        record.merchant_name || '',
        record.merchant_id || '',
        record.transaction_type || '',
        record.amount || 0,
      ];
      const tujuan = isCashIn ? [] : [
        record.destination_number || '',
        record.destination_name || '',
        record.destination_bank || '',
      ];
      return [...base, ...tujuan, record.reference_id || '', record.status || '', record.description || ''];
    });

    const totalAmount = data.reduce((sum, r) => sum + (Number(r.amount) || 0), 0);
    const totalRow = isCashIn
      ? ['Total', '', '', '', totalAmount, '', '', '']
      : ['Total', '', '', '', totalAmount, '', '', '', '', '', ''];
    rows.push([]);
    rows.push(totalRow);

    const csvContent = [
      headers.map(esc).join(','),
      ...rows.map(row => row.map(esc).join(','))
    ].join('\n');

    const blob = new Blob(['﻿' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    const dateStr = filters.date_range
      ? `${filters.date_range[0].format('YYYYMMDD')}-${filters.date_range[1].format('YYYYMMDD')}`
      : moment().format('YYYYMMDD');

    link.setAttribute('href', url);
    link.setAttribute('download', `${tabLabel}_${dateStr}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    message.success({ content: `${data.length} baris ${tabLabel.replace('_', ' ')} berhasil di-export`, key: 'csv-export' });
  };

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ marginBottom: '24px' }}>
        <Title level={2}>Cash Flow Management</Title>
        <Text type="secondary">
          Laporan arus kas masuk dan keluar merchant berdasarkan transaksi real-time
        </Text>
      </div>

      {/* Summary Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Total Cash In Today"
              value={summary.total_cash_in_today || 0}
              formatter={(value) => formatCurrency(value)}
              prefix={<RiseOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Total Cash Out Today"
              value={summary.total_cash_out_today || 0}
              formatter={(value) => formatCurrency(value)}
              prefix={<FallOutlined />}
              valueStyle={{ color: '#ff4d4f' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Net Cash Flow Today"
              value={(summary.total_cash_in_today || 0) - (summary.total_cash_out_today || 0)}
              formatter={(value) => formatCurrency(value)}
              prefix={<WalletOutlined />}
              valueStyle={{
                color: ((summary.total_cash_in_today || 0) - (summary.total_cash_out_today || 0)) >= 0 ? '#52c41a' : '#ff4d4f'
              }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Active Transactions"
              value={summary.total_transactions_today || 0}
              prefix={<Badge status="processing" />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Filters */}
      <Card style={{ marginBottom: '24px' }}>
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} sm={8} md={6}>
            <Input
              placeholder="Search merchant..."
              prefix={<SearchOutlined />}
              value={filters.search}
              onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
              allowClear
            />
          </Col>
          <Col xs={24} sm={8} md={4}>
            <Select
              placeholder="Status"
              allowClear
              style={{ width: '100%' }}
              value={filters.status}
              onChange={(value) => setFilters(prev => ({ ...prev, status: value }))}
            >
              <Option value="success">Success</Option>
              <Option value="pending">Pending</Option>
              <Option value="failed">Failed</Option>
              <Option value="processing">Processing</Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} md={5}>
            <RangePicker
              style={{ width: '100%' }}
              value={filters.date_range}
              onChange={(dates) => setFilters(prev => ({ ...prev, date_range: dates }))}
              format="DD/MM/YYYY"
            />
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Space>
              <Button
                icon={<FilterOutlined />}
                onClick={resetFilters}
              >
                Reset
              </Button>
              <Button
                type="primary"
                icon={<ReloadOutlined />}
                onClick={() => {
                  fetchCashInData();
                  fetchCashOutData();
                  fetchSummary();
                }}
              >
                Refresh
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Tabs for Cash In/Out */}
      <Card>
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          type="card"
          tabBarExtraContent={
            <Button type="primary" icon={<ExportOutlined />} onClick={exportToCSV}>
              Export Data
            </Button>
          }
        >
          <TabPane
            tab={
              <span>
                <RiseOutlined style={{ color: '#52c41a' }} />
                Cash In ({cashInPagination.total})
              </span>
            }
            key="cash-in"
          >
            <div style={{ marginBottom: '16px' }}>
              <Text type="secondary">
                Dana masuk dari transaksi QRIS, VA, E-Wallet, deposit merchant, dan refund
              </Text>
            </div>
            <Table
              columns={getCommonColumns(true)}
              dataSource={cashInData}
              rowKey="id"
              loading={loading}
              pagination={{
                current: cashInPagination.page,
                pageSize: cashInPagination.pageSize,
                total: cashInPagination.total,
                showSizeChanger: true,
                pageSizeOptions: ['20', '50', '100'],
                showQuickJumper: true,
                showTotal: (total, range) =>
                  `${range[0]}-${range[1]} dari ${total} transaksi`,
                onChange: (page, pageSize) => fetchCashInData(page, pageSize),
              }}
              scroll={{ x: 1400 }}
              summary={(pageData) => {
                const totalAmount = pageData.reduce((sum, record) => sum + (Number(record.amount) || 0), 0);
                return (
                  <Table.Summary fixed>
                    <Table.Summary.Row>
                      <Table.Summary.Cell index={0} colSpan={3}>
                        <Text strong>Total di halaman ini:</Text>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={3}>
                        <Text strong style={{ color: '#52c41a' }}>
                          +{formatCurrency(totalAmount)}
                        </Text>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={4} colSpan={4} />
                    </Table.Summary.Row>
                  </Table.Summary>
                );
              }}
            />
          </TabPane>

          <TabPane
            tab={
              <span>
                <FallOutlined style={{ color: '#ff4d4f' }} />
                Cash Out ({cashOutPagination.total})
              </span>
            }
            key="cash-out"
          >
            <div style={{ marginBottom: '16px' }}>
              <Text type="secondary">
                Dana keluar untuk top-up, transfer BIFAST, bayar tagihan, withdrawal merchant, dan fee
              </Text>
            </div>
            <Table
              columns={getCommonColumns(false)}
              dataSource={cashOutData}
              rowKey="id"
              loading={loading}
              pagination={{
                current: cashOutPagination.page,
                pageSize: cashOutPagination.pageSize,
                total: cashOutPagination.total,
                showSizeChanger: true,
                pageSizeOptions: ['20', '50', '100'],
                showQuickJumper: true,
                showTotal: (total, range) =>
                  `${range[0]}-${range[1]} dari ${total} transaksi`,
                onChange: (page, pageSize) => fetchCashOutData(page, pageSize),
              }}
              scroll={{ x: 1400 }}
              summary={(pageData) => {
                const totalAmount = pageData.reduce((sum, record) => sum + (Number(record.amount) || 0), 0);
                return (
                  <Table.Summary fixed>
                    <Table.Summary.Row>
                      <Table.Summary.Cell index={0} colSpan={3}>
                        <Text strong>Total di halaman ini:</Text>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={3}>
                        <Text strong style={{ color: '#ff4d4f' }}>
                          -{formatCurrency(Math.abs(totalAmount))}
                        </Text>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={4} colSpan={4} />
                    </Table.Summary.Row>
                  </Table.Summary>
                );
              }}
            />
          </TabPane>
        </Tabs>
      </Card>

      {/* Detail Modal */}
      <Modal
        title="Detail Transaksi Cash Flow"
        open={detailModalVisible}
        onCancel={() => {
          setDetailModalVisible(false);
          setSelectedRecord(null);
        }}
        footer={null}
        width={700}
      >
        {selectedRecord && (
          <div>
            <Descriptions bordered size="small" column={2}>
              <Descriptions.Item label="Transaction ID" span={2}>
                {selectedRecord.transaction_id || selectedRecord.reference_id}
              </Descriptions.Item>
              <Descriptions.Item label="Merchant">
                {selectedRecord.merchant_name}
              </Descriptions.Item>
              <Descriptions.Item label="Merchant ID">
                {selectedRecord.merchant_id}
              </Descriptions.Item>
              <Descriptions.Item label="Tipe Transaksi">
                {getTransactionTypeTag(selectedRecord.transaction_type)}
              </Descriptions.Item>
              <Descriptions.Item label="Status">
                {getStatusTag(selectedRecord.status)}
              </Descriptions.Item>
              <Descriptions.Item label="Amount">
                <Text strong style={{
                  color: selectedRecord.amount >= 0 ? '#52c41a' : '#ff4d4f',
                  fontSize: '16px'
                }}>
                  {selectedRecord.amount >= 0 ? '+' : ''}{formatCurrency(selectedRecord.amount)}
                </Text>
              </Descriptions.Item>
              <Descriptions.Item label="Fee">
                {formatCurrency(selectedRecord.fee || 0)}
              </Descriptions.Item>
              <Descriptions.Item label="Payment Method">
                {selectedRecord.payment_method || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Reference">
                {selectedRecord.reference_id || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Waktu Transaksi" span={2}>
                {moment(selectedRecord.created_at).format('DD/MM/YYYY HH:mm:ss')}
              </Descriptions.Item>
              <Descriptions.Item label="Description" span={2}>
                {selectedRecord.description || '-'}
              </Descriptions.Item>
              {selectedRecord.external_reference && (
                <Descriptions.Item label="External Reference" span={2}>
                  {selectedRecord.external_reference}
                </Descriptions.Item>
              )}
            </Descriptions>

            {selectedRecord.metadata && (
              <div style={{ marginTop: '16px' }}>
                <Divider>Additional Information</Divider>
                <pre style={{
                  backgroundColor: '#f5f5f5',
                  padding: '12px',
                  borderRadius: '4px',
                  fontSize: '12px',
                  overflow: 'auto',
                  maxHeight: '200px'
                }}>
                  {JSON.stringify(JSON.parse(selectedRecord.metadata), null, 2)}
                </pre>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default CashInOut;